/**
 * Common templates, classes, and utilities for InstantSearch widgets
 * All styling done via Tailwind classes
 */

/**
 * Refinement list item template with Tailwind
 */
export const itemTemplate = ({
  label,
  count,
  isRefined,
  inputType = 'checkbox',
}) => `
  <label class="flex items-center justify-between cursor-pointer group">
    <div class="flex items-center gap-2">
      <input
        type="${inputType}"
        class="${inputType === 'radio' ? 'input-radio' : 'input-check'}"
        ${isRefined ? 'checked="true"' : ''}
      >
      <span class="text-sm text-gray-700 group-hover:text-gray-900 js-apply-italic-i">${label}</span>
    </div>
    <span class="text-xs text-gray-400">${count}</span>
  </label>
`;

/**
 * Shared translatable text defaults — overridden via setTexts()
 */
export const texts = {
  showMore: 'Show more',
  showLess: 'Show less',
  clearFilters: 'Clear filters',
  rangeSeparator: 'to',
  rangeSubmit: 'Go',
  resultSingular: 'result',
  resultPlural: 'results',
  showing: 'Showing',
  of: 'of',
  remove: 'Remove',
  all: 'All',
  searchPlaceholder: 'Search...',
  loading: 'Loading...',
  allResults: 'All results:',
  noRange: 'No range available with current filters',
  errorUnavailable: 'Search service is temporarily unavailable.',
  errorRateLimit: 'Search rate limit reached. Please try again in a moment.',
  errorAuth: 'Search service configuration error.',
  errorNetwork: 'Network error. Please check your connection.',
  errorConfig:
    'Algolia configuration missing. Search is temporarily unavailable.',
  untitled: 'Untitled',
};

/**
 * Populate texts from a container's data-text-* attributes
 */
export function setTexts(el) {
  if (!el?.dataset) return;
  Object.keys(texts).forEach((key) => {
    const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
    if (el.dataset[attr] !== undefined) {
      texts[key] = el.dataset[attr];
    }
  });
}

/**
 * Show more button template
 */
export const showMoreTemplate = ({ isShowingMore }) => `
  <button class="mt-2 text-sm text-gray-600 underline hover:text-gray-900">
    ${isShowingMore ? texts.showLess : texts.showMore}
  </button>
`;

/**
 * Search box template for refinement lists
 */
export const searchableTemplate = {
  searchableInput: `
    <input
      type="search"
      class="input-text mb-3"
      placeholder="{{placeholder}}"
    >
  `,
  searchableSubmit: '',
  searchableReset: '',
};

/**
 * Common CSS classes for widgets
 */
export const commonClasses = {
  item: ['cursor-pointer', 'py-1'],
  selectedItem: ['font-medium'],
  list: ['space-y-1'],
  noRefinementRoot: ['opacity-40', 'pointer-events-none'],
  showMore: ['mt-2'],
  count: ['text-xs', 'text-gray-400', 'ml-auto'],
};

/**
 * Panel wrapper classes
 */
export const panelClasses = {
  root: ['mb-5'],
  header: [
    'group',
    'flex',
    'items-center',
    'justify-between',
    'mb-3',
    'cursor-pointer',
  ],
  body: [],
  footer: [],
  collapsedRoot: ['s-collapsed'],
  collapseButton: [
    'text-gray-400',
    'group-hover:text-gray-900',
    'transition-colors',
    'duration-[var(--duration-subtle)]',
    'ease-[var(--ease-smooth)]',
    'cursor-pointer',
  ],
  collapseIcon: ['size-4'],
};

/**
 * Panel templates
 */
export const panelTemplates = {
  header: ({ widgetParams }) => `
    <span class="text-sm font-semibold text-gray-900 uppercase tracking-wide transition-colors duration-[var(--duration-subtle)] ease-[var(--ease-smooth)] group-hover:text-secondary">
      ${widgetParams.label || widgetParams.attribute}
    </span>
  `,
  collapseButtonText: ({ collapsed }) => `
    <svg class="size-4 pointer-events-none transition-transform ${collapsed ? '' : 'rotate-180'}" aria-hidden="true"><use href="#icon-chevron-down"></use></svg>
  `,
};

/**
 * Check if panel should be collapsed based on data attribute
 * Defaults to open (false) unless data-collapsed="true"
 */
export const isCollapsed = (container) => {
  const el =
    typeof container === 'string'
      ? document.querySelector(container)
      : container;
  return el?.dataset.collapsed === 'true';
};

/**
 * Stats template — compact hit count used in results header.
 */
export const statsTemplate = ({ nbHits, processingTimeMS }) => `
  <span class="text-sm text-gray-600">
    ${nbHits.toLocaleString()} ${nbHits === 1 ? texts.resultSingular : texts.resultPlural}
    <span class="text-gray-400">(${processingTimeMS}ms)</span>
  </span>
`;

/**
 * Pagination count template — mirrors Twig pagination.twig "Showing X–Y of Z"
 * format. Pass a custom label (e.g. "robots") to override the default
 * result/results wording; falls back to singular/plural from texts.
 */
export const paginationCountTemplate =
  (label) =>
  ({ nbHits, page, hitsPerPage }) => {
    if (!nbHits) return '';
    const from = page * hitsPerPage + 1;
    const to = Math.min((page + 1) * hitsPerPage, nbHits);
    const itemLabel =
      label || (nbHits === 1 ? texts.resultSingular : texts.resultPlural);
    return `
    ${texts.showing} <span class="font-semibold">${from}</span>
    – <span class="font-semibold">${to}</span>
    ${texts.of} <span class="font-semibold">${nbHits.toLocaleString()}</span> ${itemLabel}
  `;
  };

/**
 * Clear refinements template
 */
export const clearRefinementsTemplate = {
  get resetLabel() {
    return texts.clearFilters;
  },
};

/**
 * Range input template
 */
export const rangeInputTemplate = {
  get separatorText() {
    return `<span class="mx-2 text-gray-400">${texts.rangeSeparator}</span>`;
  },
  get submitText() {
    return `<button class="btn btn-primary btn-small ml-2">${texts.rangeSubmit}</button>`;
  },
};

/**
 * Pagination cssClasses — mirrors markup shape of Twig pagination include.
 * Selected/disabled styling is driven by CSS (see _components.inputs.css).
 */
export const paginationClasses = {
  root: '',
  list: 'pagination-list',
  item: 'pagination-item',
  link: 'pagination-link',
  selectedItem: 'pagination-item--selected',
  disabledItem: 'pagination-item--disabled',
  firstPageItem: 'pagination-item--first',
  previousPageItem: 'pagination-item--prev',
  pageItem: 'pagination-item--page',
  nextPageItem: 'pagination-item--next',
  lastPageItem: 'pagination-item--last',
};

/**
 * Current refinements classes
 */
export const currentRefinementsClasses = {
  root: [''],
  list: ['flex', 'flex-wrap', 'gap-2'],
  item: ['inline-flex', 'items-center', 'gap-1.5', 'py-1', 'text-sm'],
  label: ['text-gray-600'],
  category: ['text-gray-900', 'font-medium'],
  delete: [
    'relative',
    '-mr-0.5',
    'p-0.5',
    'text-gray-500',
    'hover:text-gray-900',
    'cursor-pointer',
    'transition-colors',
    'before:absolute',
    'before:-inset-2.5',
    'before:content-[""]',
  ],
};
